import Network from 'lib/api/network';

const defaultTemplate = { id: null, title: '', description: '', message: '', gifUrl: null, points: 500, recipients: [] };

export default {
  namespaced: true,

  state() {
    return {
      pointsConfig: null,
      dollarsPerPoint: null,
      eventTemplates: [],
      activeTemplate: { ...defaultTemplate },
    };
  },

  mutations: {
    setActiveTemplate(state, eventTemplate) {
      let activeTemplate = { ...defaultTemplate };
      if (eventTemplate) {
        const {
 id, title, description, message, points,
} = eventTemplate;
        const gifUrl = eventTemplate.image_url;
        activeTemplate = { ...activeTemplate, id, title, description, message, gifUrl, points };
      }
      state.activeTemplate = activeTemplate;
    },

    updateActiveTemplate(state, { key, value }) {
      state.activeTemplate[key] = value;
    },

    updateEventTemplates(state, { eventTemplateId, eventTemplate }) {
      const idx = state.eventTemplates.findIndex(et => et.id === eventTemplateId);
      if (eventTemplate) {
        if (idx === -1) { // eventTemplate present with no matching id in the list. It's a new one, add it to the bottom.
          state.eventTemplates.push(eventTemplate);
        } else { // eventTemplate present with a matching id in the list. Editing an existing one. Replace it.
          state.eventTemplates[idx] = eventTemplate;
        }
      } else { // No eventTemplate present, we're deleting a particular id
        state.eventTemplates.splice(idx, 1);
      }
    },
  },

  actions: {
    openActiveTemplateModal({ commit }, { modalType, eventTemplate }) {
      const subcomponent = modalType === 'send' ? 'bonus-event-templates-modals-send' : 'bonus-event-templates-modals-configure';
      commit('setActiveTemplate', eventTemplate);
      commit('updateModal', { visible: true, subcomponent, size: 'xl', dismissable: false }, { root: true });
    },

    upsertActiveTemplate({ state, commit, dispatch }) {
      commit('update', { key: 'formSubmitted', value: true }, { root: true });
      return new Promise(() => {
        const {
 id, title, description, message, gifUrl, points,
} = state.activeTemplate;
        const path = `/bonus/event_templates${id ? `/${id}` : ''}`;
        const params = { bonus_event_templates: { title, description, message, image_url: gifUrl, points } };
        Network.post(path, params, {
          success: ({ eventTemplate }) => {
            commit('updateEventTemplates', { eventTemplateId: eventTemplate.id, eventTemplate });
            dispatch('setSuccessToast', `Successfully ${id ? 'updated' : 'created'} "${eventTemplate.title}"`, { root: true });
          },
          error: (data) => {
            dispatch('setErrorToast', data.responseJSON?.error, { root: true });
          },
          complete: () => {
            commit('closeModal', null, { root: true });
            commit('update', { key: 'formSubmitted', value: false }, { root: true });
          },
        });
      });
    },

    sendActiveTemplate({ state, commit, dispatch }) {
      commit('update', { key: 'formSubmitted', value: true }, { root: true });
      const {
 id, title, message, gifUrl, points, recipients,
} = state.activeTemplate;
      const sendEventPromises = recipients.map((recipient) => {
        const params = { bonus_event: { recipient_id: recipient.id, template_id: id, points, message, image_url: gifUrl } };
        return dispatch('sendEventToRecipient', { params });
      });
      Promise.all(sendEventPromises)
        .then((results) => {
          const partition = { successes: [], failures: [], tooSoon: [] };
          results.forEach((result, idx) => {
            // Note that the results of Promise.all is an array in the same order as the promises you put into it.
            // In this case, it means that the results of each promise corresponds to the order of the recipients.
            const recipient = recipients[idx];
            if (result.success) {
              partition.successes.push(recipient);
            } else if (result.error === 'too_soon') {
              partition.tooSoon.push(recipient);
            } else {
              partition.failures.push(recipient);
            }
          });
          commit('updateModal', {
            visible: true,
            subcomponent: 'bonus-event-templates-modals-send-confirmation',
            data: { ...partition, title, points },
          }, { root: true });
          commit('update', { key: 'formSubmitted', value: false }, { root: true });
        });
    },

    sendEventToRecipient(_store, { params }) {
      return new Promise((resolve) => {
        Network.post('/bonus/events', params, {
          success: () => { resolve({ success: true }); },
          error: (error) => { resolve({ success: false, error: error.responseJSON?.error }); },
        });
      });
    },

    deleteEventTemplate({ commit, dispatch }, { eventTemplate }) {
      commit('update', { key: 'formSubmitted', value: true }, { root: true });
      return new Promise(() => {
        const eventTemplateId = eventTemplate.id;
        Network.delete(`/bonus/event_templates/${eventTemplateId}`, null, {
          success: () => {
            commit('updateEventTemplates', { eventTemplateId, eventTemplate: null });
            dispatch('setSuccessToast', `Successfully deleted "${eventTemplate.title}"'`, { root: true });
          },
          error: () => { dispatch('setErrorToast', null, { root: true }); },
          complete: () => {
            commit('closeModal', null, { root: true });
            commit('update', { key: 'formSubmitted', value: false }, { root: true });
          },
        });
      });
    },
  },
};
